// src/store/builders/useBuilderStatusStore.js
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { axiosInstance } from "../../lib/axiosInstance";
import useAuthStore from "../useAuthStore";

const STALE_AFTER_MS = 5 * 60 * 1000; // 5 minutes

const initialState = {
  status: null,
  reason: null,
  isLoading: false,
  error: null,
  lastFetchedAt: null, // tracks when we last fetched to avoid refetching on every page
};

const useBuilderStatusStore = create(
  persist(
    (set, get) => ({
      ...initialState,

      fetchBuilderStatus: async () => {
        const { token, user } = useAuthStore.getState();

        if (!token || !user || !user.builderUniqueId) {
          set({ status: null, isLoading: false });
          return;
        }

        // ── Staleness guard ───────────────────────────────────────────
        // If we already have a status and it was fetched recently, skip the API call.
        // This prevents a network request on every page navigation (Layout mounts this on every page).
        const { status, lastFetchedAt } = get();
        const isStale = !lastFetchedAt || Date.now() - lastFetchedAt > STALE_AFTER_MS;
        if (status && !isStale) return;
        // ─────────────────────────────────────────────────────────────

        set({ isLoading: true, error: null });

        try {
          const response = await axiosInstance.get(
            `/builders/auth/status/${user.builderUniqueId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );

          if (response.data && response.data.status) {
            set({
              status: response.data.status,
              reason: response.data.reason || null,
              isLoading: false,
              error: null,
              lastFetchedAt: Date.now(),
            });
          } else {
            set({
              status: null,
              isLoading: false,
              error: "Invalid response from server",
            });
          }
        } catch (error) {
          set({
            status: null,
            isLoading: false,
            error: error.response?.data?.message || "Failed to fetch builder status",
          });
        }
      },

      setStatus: (status) => set({ status }),

      reset: () => set(initialState),
    }),
    {
      name: "builder-status-storage",
      getStorage: () => localStorage,
    }
  )
);

export default useBuilderStatusStore;